import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useWorkspaceStore } from "./use-workspace";
import { apiRequest } from "@/lib/queryClient";
import type { AdCampaign } from "@shared/schema";

export function useCampaigns() {
  const { activeWorkspaceId } = useWorkspaceStore();
  return useQuery<AdCampaign[]>({
    queryKey: ["/api/workspaces", activeWorkspaceId, "campaigns"],
    queryFn: async () => {
      if (!activeWorkspaceId) return [];
      const res = await fetch(`/api/workspaces/${activeWorkspaceId}/campaigns`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to fetch campaigns");
      return res.json();
    },
    enabled: !!activeWorkspaceId,
  });
}

export function useCreateCampaign() {
  const queryClient = useQueryClient();
  const { activeWorkspaceId } = useWorkspaceStore();
  return useMutation({
    mutationFn: async (data: any) => {
      const res = await apiRequest("POST", `/api/workspaces/${activeWorkspaceId}/campaigns`, data);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/workspaces", activeWorkspaceId, "campaigns"] });
    },
  });
}

export function useUpdateCampaign() {
  const queryClient = useQueryClient();
  const { activeWorkspaceId } = useWorkspaceStore();
  return useMutation({
    mutationFn: async ({ id, ...data }: { id: number } & Record<string, any>) => {
      const res = await apiRequest("PUT", `/api/campaigns/${id}`, data);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/workspaces", activeWorkspaceId, "campaigns"] });
    },
  });
}

export function useDeleteCampaign() {
  const queryClient = useQueryClient();
  const { activeWorkspaceId } = useWorkspaceStore();
  return useMutation({
    mutationFn: async (id: number) => {
      await apiRequest("DELETE", `/api/campaigns/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/workspaces", activeWorkspaceId, "campaigns"] });
    },
  });
}
